import isEqual from "lodash/isEqual";

const OPTION_FIELDS = [
	{ optionKey: "integrationTypes", field: "integrationType" },
	{ optionKey: "countryCodes", field: "restrictedCountries" },
	{ optionKey: "currencyCodes", field: "currency" },
];

const isBlank = (value) => value === null || value === undefined || value === "";

const parseDecimal = (rawValue) => {
	const raw = String(rawValue);
	const match = raw.match(/^[+-]?(?:(\d+)(?:\.(\d*))?|\.(\d+))(?:[eE]([+-]?\d+))?$/);
	if (!match) return null;

	const value = Number(raw);
	if (!Number.isFinite(value)) return null;

	const fractionLength = (match[2] || match[3] || "").length;
	const exponent = Number(match[4] || 0);

	return {
		value,
		decimalPlaces: Math.max(fractionLength - exponent, 0),
	};
};

const parseNonNegativeInteger = (rawValue) => {
	const parsed = parseDecimal(rawValue);
	if (!parsed || parsed.value < 0 || !Number.isInteger(parsed.value)) return null;
	return parsed.value;
};

const parsePositiveInteger = (rawValue) => {
	const parsed = parseDecimal(rawValue);
	if (!parsed || parsed.value <= 0 || !Number.isInteger(parsed.value)) return null;
	return parsed.value;
};

const parseFee = (rawValue) => {
	const parsed = parseDecimal(rawValue);
	if (!parsed || parsed.value < 0 || parsed.decimalPlaces > 2) return null;
	return parsed.value;
};

export const validateProviderOptions = (options) => {
	const errors = {};

	OPTION_FIELDS.forEach(({ optionKey, field }) => {
		const values = options && options[optionKey];
		if (!Array.isArray(values)) {
			errors[field] = `Backend options.${optionKey} must be an array.`;
			return;
		}

		if (values.some((value) => typeof value !== "string" || value.length === 0)) {
			errors[field] = `Backend options.${optionKey} must contain only non-empty strings.`;
			return;
		}

		if (optionKey === "integrationTypes" && values.some((value) => value !== value.toLowerCase())) {
			errors[field] = "Backend options.integrationTypes must contain lowercase codes.";
		}
	});

	return errors;
};

export const parseStoredProviderCodeArray = (rawValue) => {
	if (typeof rawValue !== "string") {
		return { error: "Stored value must be JSON text." };
	}

	let parsed;
	try {
		parsed = JSON.parse(rawValue);
	} catch (error) {
		return { error: "Stored value is malformed JSON." };
	}

	if (!Array.isArray(parsed)) {
		return { error: "Stored value must be a JSON array." };
	}

	if (parsed.some((value) => typeof value !== "string")) {
		return { error: "Stored JSON array must contain only string codes." };
	}

	return { value: parsed };
};

export const parseStoredProviderCountries = (rawValue) => {
	if (typeof rawValue !== "string") return { error: "Stored value must be text." };
	if (rawValue.includes('"')) return parseStoredProviderCodeArray(rawValue);
	const trimmed = rawValue.trim();
	const hasBrackets = trimmed.startsWith("[") && trimmed.endsWith("]");
	const content = hasBrackets ? trimmed.slice(1, -1).trim() : trimmed;
	if (!content) return { value: [] };
	const tokens = content.split(",").map((token) => token.trim());
	if (tokens.some((token) => !/^(?:[A-Za-z]+|'[A-Za-z]+')$/.test(token))) {
		return { error: "Stored country list cannot be read. Replace it to change this field." };
	}
	return { value: tokens.map((token) => token.replace(/'/g, "")) };
};

const validateCodeArray = (value, domain, field, allowEmpty, errors) => {
	if (!Array.isArray(value)) {
		errors[field] = "Value must be an array of codes.";
		return;
	}

	if (!allowEmpty && value.length === 0) {
		errors[field] = "Select at least one code.";
		return;
	}

	if (value.some((code) => typeof code !== "string" || !domain.includes(code))) {
		errors[field] = "Value contains a code not present in backend options.";
	}
};

export const validateEffectiveProvider = (values, options, booleanMode = "edit", partial = false) => {
	const hasField = (field) => !partial || Object.prototype.hasOwnProperty.call(values, field);
	const errors = Object.fromEntries(Object.entries(validateProviderOptions(options)).filter(([field]) => hasField(field)));
	const payload = {};

	if (hasField("name") && (typeof values.name !== "string" || values.name.trim().length === 0)) {
		errors.name = "Name is required and cannot contain only whitespace.";
	} else if (hasField("name")) {
		payload.name = values.name;
	}

	if (hasField("path") && (typeof values.path !== "string" || values.path.length === 0)) {
		errors.path = "Path is required.";
	} else if (hasField("path")) {
		payload.path = values.path;
	}

	const order = parseNonNegativeInteger(values.order);
	if (hasField("order") && order === null) {
		errors.order = "Order must be a finite non-negative integer.";
	} else if (hasField("order")) {
		payload.order = order;
	}

	const booleanValues = [true, false, 1, 0];
	["listed", "active"].forEach((field) => {
		if (!hasField(field)) return;
		const value = values[field];
		if ((booleanMode === "add" && typeof value !== "boolean") || !booleanValues.includes(value)) {
			errors[field] = "Select True or False.";
		} else {
			payload[field] = value;
		}
	});

	if (hasField("integrationType") && (typeof values.integrationType !== "string" || values.integrationType.length === 0)) {
		errors.integrationType = "Integration Type is required.";
	} else if (hasField("integrationType") && !errors.integrationType && !options.integrationTypes.includes(values.integrationType)) {
		errors.integrationType = "Select an Integration Type from backend options.";
	} else if (hasField("integrationType")) {
		payload.integrationType = values.integrationType;
	}

	if (hasField("restrictedCountries") && !errors.restrictedCountries) {
		validateCodeArray(values.restrictedCountries, options.countryCodes, "restrictedCountries", true, errors);
	}
	if (hasField("restrictedCountries") && !errors.restrictedCountries) payload.restrictedCountries = values.restrictedCountries;

	if (hasField("currency") && !errors.currency) {
		validateCodeArray(values.currency, options.currencyCodes, "currency", false, errors);
	}
	if (hasField("currency") && !errors.currency) payload.currency = values.currency;

	const fee = parseFee(values.fee);
	if (hasField("fee") && fee === null) {
		errors.fee = "Fee must be a finite non-negative number with at most two decimal places.";
	} else if (hasField("fee")) {
		payload.fee = fee;
	}

	return {
		errors,
		payload: Object.keys(errors).length === 0 ? payload : null,
	};
};

export const prepareAddProviderPayload = (values, options) => {
	const result = validateEffectiveProvider(
		{
			...values,
			order: isBlank(values.order) ? 0 : values.order,
			fee: isBlank(values.fee) ? 0 : values.fee,
		},
		options,
		"add",
	);

	const errors = { ...result.errors };
	let id;
	if (!isBlank(values.id)) {
		id = parsePositiveInteger(values.id);
		if (id === null) errors.id = "ID must be a positive integer.";
	}

	if (!result.payload || Object.keys(errors).length > 0) return { errors, payload: null };

	return {
		errors,
		payload: isBlank(values.id) ? result.payload : { id, ...result.payload },
	};
};

export const prepareEditProviderPayload = (id, values, options, initialValues = null) => {
	const changes = {};
	["name", "path", "order", "listed", "active", "integrationType", "restrictedCountries", "currency", "fee"].forEach((field) => {
		const value = field === "listed" || field === "active" ? values[field]?.value ?? null : values[field];
		const isCodeArray = field === "restrictedCountries" || field === "currency";
		const unchanged = initialValues && isCodeArray && Array.isArray(values[field]) && Array.isArray(initialValues[field])
			? isEqual([...values[field]].sort(), [...initialValues[field]].sort())
			: initialValues && isEqual(values[field], initialValues[field]);
		if (initialValues ? unchanged : value === null) return;
		changes[field] = value;
	});
	const result = validateEffectiveProvider(changes, options, "edit", true);
	return result.payload ? { errors: result.errors, payload: { id, ...result.payload } } : result;
};
